import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { PraktijkdataMedewerker } from '../types';
import {
    KnownPraktijkdataConfig,
    forgetPraktijkdataConfig,
    getKnownPraktijkdataConfigs,
} from '../utils/praktijkdataConfigs';
import { logger } from '../utils/logger';

interface ConfigSectionState {
    /** null zolang de user-settings nog laden of niet beschikbaar zijn */
    medewerkerId: number | null;
    overridable: boolean;
    medewerkers: PraktijkdataMedewerker[] | null;
    loading: boolean;
    saving: boolean;
    error: string | null;
    saved: boolean;
}

/**
 * "Mijn medewerker in Praktijkdata"-koppeling op de instellingenpagina.
 * Rendert niets zolang deze gebruiker nog geen Praktijkdata-configuratie is
 * tegengekomen (bekend via localStorage, gevuld door de deel-dialoog). De
 * keuze wordt direct opgeslagen; de backend gebruikt hem daarna automatisch
 * als filter bij het zoeken van afspraken en bij DOCUMENT-exports.
 */
const PraktijkdataUserSettings: React.FC = () => {
    const { t } = useTranslation();
    const apiService = useMemo(() => new ApiService(), []);

    const [configs, setConfigs] = useState<KnownPraktijkdataConfig[]>(() => getKnownPraktijkdataConfigs());
    const [sections, setSections] = useState<Record<string, ConfigSectionState>>({});

    const patchSection = (configId: string, patch: Partial<ConfigSectionState>) => {
        const defaults: ConfigSectionState = {
            medewerkerId: null,
            overridable: false,
            medewerkers: null,
            loading: true,
            saving: false,
            error: null,
            saved: false,
        };
        setSections((prev) => ({
            ...prev,
            [configId]: { ...defaults, ...prev[configId], ...patch },
        }));
    };

    useEffect(() => {
        let cancelled = false;

        configs.forEach(async (config) => {
            patchSection(config.id, { loading: true });
            try {
                const settings = await apiService.getExportUserSettings(config.id);
                if (cancelled) return;
                const overridable = settings.overridableFields.includes('medewerker_id');
                const rawId = settings.settings?.medewerker_id;
                patchSection(config.id, {
                    overridable,
                    medewerkerId: typeof rawId === 'number' ? rawId : null,
                });
                if (!overridable) {
                    patchSection(config.id, { loading: false });
                    return;
                }
                // De medewerkers-lijst komt via de backend uit het EPD; dat kan
                // even duren, vandaar de spinner per configuratie.
                const medewerkers = await apiService.getPraktijkdataMedewerkers(config.id);
                if (cancelled) return;
                patchSection(config.id, { medewerkers, loading: false });
            } catch (error: any) {
                if (cancelled) return;
                const status = error?.response?.status;
                logger.error('Praktijkdata user-settings load failed with status:', status);
                if (status === 404 || status === 403) {
                    // Configuratie bestaat niet meer (of is niet meer van deze
                    // gebruiker): vergeten en de sectie verbergen.
                    forgetPraktijkdataConfig(config.id);
                    setConfigs((prev) => prev.filter((item) => item.id !== config.id));
                    return;
                }
                patchSection(config.id, {
                    loading: false,
                    error: t('userSettings.praktijkdata.loadError'),
                });
            }
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveMedewerker = async (configId: string, value: string) => {
        // Lege keuze = override wissen (backend verwacht daarvoor null).
        const parsed = value === '' ? null : Number(value);
        patchSection(configId, { saving: true, error: null, saved: false });
        try {
            await apiService.updateExportUserSettings(configId, { medewerker_id: parsed });
            patchSection(configId, { medewerkerId: parsed, saving: false, saved: true });
        } catch (error: any) {
            logger.error('Praktijkdata user-settings save failed with status:', error?.response?.status);
            patchSection(configId, {
                saving: false,
                error: t('userSettings.praktijkdata.saveError'),
            });
        }
    };

    // Alleen configuraties tonen waar de koppeling ook echt kan. Pas na de
    // user-settings, want een partnerkoppeling (geen medewerker_id) is vooraf
    // niet te herkennen.
    const visibleConfigs = configs.filter((config) => {
        const section = sections[config.id];
        return Boolean(section && (section.overridable || (!section.loading && section.error)));
    });

    if (visibleConfigs.length === 0) {
        return null;
    }

    return (
        <div className="mb-6">
            <div className="flex items-center mb-4">
                <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900">{t('userSettings.praktijkdata.heading')}</h2>
            </div>

            <div className="space-y-4">
                {visibleConfigs.map((config) => {
                    const section = sections[config.id];
                    return (
                        <div key={config.id}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {visibleConfigs.length > 1 && config.name
                                    ? t('userSettings.praktijkdata.labelWithConfig', { name: config.name })
                                    : t('userSettings.praktijkdata.label')}
                            </label>
                            {!section || section.loading ? (
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <div className="w-4 h-4 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
                                    {t('userSettings.praktijkdata.loading')}
                                </div>
                            ) : (
                                <>
                                    {section.medewerkers && (
                                        <select
                                            value={section.medewerkerId !== null ? String(section.medewerkerId) : ''}
                                            onChange={(e) => saveMedewerker(config.id, e.target.value)}
                                            disabled={section.saving}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-600 focus:border-primary-600 disabled:opacity-50"
                                        >
                                            <option value="">{t('userSettings.praktijkdata.none')}</option>
                                            {section.medewerkers.map((medewerker) => (
                                                <option key={medewerker.id} value={medewerker.id}>
                                                    {medewerker.name}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    {section.saved && !section.saving && (
                                        <p className="mt-1 text-xs text-green-600">
                                            {t('userSettings.praktijkdata.saved')}
                                        </p>
                                    )}
                                    {section.error && (
                                        <p className="mt-1 text-xs text-red-600">{section.error}</p>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
                <p className="text-xs text-gray-500">{t('userSettings.praktijkdata.hint')}</p>
            </div>
        </div>
    );
};

export default PraktijkdataUserSettings;
